import CloseIcon from "@mui/icons-material/Close"
import { Alert, AlertTitle, Button, IconButton, type AlertColor } from "@mui/material"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import type { PublicMessage } from "appsShared/inAppMessages"

const ALERT_SEVERITY: Record<string, AlertColor> = {
  info: "info",
  warning: "warning",
  outage: "error",
}

/** Spec §Engine › Rendering: `/…` navigates inside the app, `https://…` opens a new tab, anything else is dropped. */
export function actionTarget(
  href: string,
): { kind: "route" | "external"; href: string } | null {
  if (href.startsWith("https://")) return { kind: "external", href }
  if (href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/\\"))
    return { kind: "route", href }
  return null
}

export const messageLocale = (language: string): "fr" | "en" =>
  language.startsWith("en") ? "en" : "fr"

type Props = {
  message: PublicMessage
  /** Undefined renders no close control (`dismiss: none`). */
  onClose?: () => void
  square?: boolean
}

/**
 * One message. Text renders as React text nodes only — never HTML (spec §Security).
 */
export const InAppMessageCard: FC<Props> = ({ message, onClose, square }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = messageLocale(i18n.language ?? "fr")
  const target = message.action ? actionTarget(message.action.href) : null

  return (
    <Alert
      severity={ALERT_SEVERITY[message.severity ?? "info"]}
      square={square}
      data-message-id={message.id}
      sx={{ alignItems: "center", ...(square && { borderRadius: 0 }) }}
      // MUI drops its own close button when `action` is set, so both live here.
      action={
        <>
          {target?.kind === "external" && message.action && (
            <Button
              color="inherit"
              size="small"
              component="a"
              href={target.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {message.action.label[locale]}
            </Button>
          )}
          {target?.kind === "route" && message.action && (
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(target.href)}
            >
              {message.action.label[locale]}
            </Button>
          )}
          {onClose && (
            <IconButton
              color="inherit"
              size="small"
              aria-label={t("close")}
              title={t("close")}
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </>
      }
    >
      {message.title && <AlertTitle>{message.title[locale]}</AlertTitle>}
      {message.body[locale]}
    </Alert>
  )
}
